import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Select,
  Container,
  FormErrorMessage,
  HStack,
  Link,
} from "@chakra-ui/react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types/user";

interface LoginForm {
  email: string;
  password: string;
  role: UserRole;
}

interface LoginErrors {
  email?: string;
  password?: string;
  role?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<LoginForm>({
    email: "",
    password: "",
    role: "buyer",
  });
  const [errors, setErrors] = useState<LoginErrors>({});

  const validateForm = () => {
    const newErrors: LoginErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.role) newErrors.role = "User type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      await login(formData.email, formData.password);

      toast({
        title: "Login Successful",
        description: "Welcome back!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      switch (formData.role) {
        case "farmer":
          navigate("/dashboard");
          break;
        case "agro_dealer":
          navigate("/dashboard");
          break;
        case "buyer":
          navigate("/marketplace");
          break;
        default:
          navigate("/");
          break;
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to login";
      setErrors({
        ...errors,
        password: errorMessage.includes("password") ? errorMessage : undefined,
        email: errorMessage.includes("not found") ? errorMessage : undefined,
      });

      toast({
        title: "Login Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="white" display="flex" alignItems="center" py={8}>
      <Container maxW="420px" w="full">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            bg="white"
            border="1px solid"
            borderColor="neutral.200"
            borderRadius="12px"
            p={8}
            boxShadow="0 2px 8px rgba(0, 0, 0, 0.05)"
          >
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <VStack spacing={2} align="start">
                <Heading
                  as="h1"
                  fontSize="28px"
                  fontWeight="700"
                  color="neutral.900"
                >
                  Welcome back!
                </Heading>
                <Text fontSize="14px" color="neutral.600" lineHeight="1.5">
                  Sign in to your MarketLink account to continue
                </Text>
              </VStack>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  {/* User Type Selection */}
                  <FormControl isRequired isInvalid={!!errors.role}>
                    <FormLabel
                      fontSize="13px"
                      fontWeight="600"
                      color="neutral.900"
                      mb={2}
                    >
                      User Type
                    </FormLabel>
                    <Select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          role: e.target.value as UserRole,
                        })
                      }
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: "primary.500",
                        boxShadow: "0 0 0 1px rgba(26, 94, 63, 0.1)",
                      }}
                      fontSize="14px"
                      h="40px"
                    >
                      <option value="farmer">🌾 Farmer</option>
                      <option value="buyer">🛒 Buyer</option>
                      <option value="agro_dealer">🏪 Agro Dealer</option>
                    </Select>
                    {errors.role && (
                      <FormErrorMessage fontSize="12px">
                        {errors.role}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Email Input */}
                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel
                      fontSize="13px"
                      fontWeight="600"
                      color="neutral.900"
                      mb={2}
                    >
                      Email Address
                    </FormLabel>
                    <Input
                      type="email"
                      placeholder="name@example.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: "primary.500",
                        boxShadow: "0 0 0 1px rgba(26, 94, 63, 0.1)",
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.email && (
                      <FormErrorMessage fontSize="12px">
                        {errors.email}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Password Input */}
                  <FormControl isRequired isInvalid={!!errors.password}>
                    <HStack justify="space-between" w="full" mb={2}>
                      <FormLabel
                        fontSize="13px"
                        fontWeight="600"
                        color="neutral.900"
                        m={0}
                      >
                        Password
                      </FormLabel>
                      <Link
                        as={RouterLink}
                        to="/forgot-password"
                        fontSize="12px"
                        color="primary.500"
                        fontWeight="600"
                        _hover={{
                          color: "primary.600",
                          textDecoration: "underline",
                        }}
                      >
                        Forgot Password?
                      </Link>
                    </HStack>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          password: e.target.value,
                        })
                      }
                      borderColor="neutral.200"
                      _focus={{
                        borderColor: "primary.500",
                        boxShadow: "0 0 0 1px rgba(26, 94, 63, 0.1)",
                      }}
                      fontSize="14px"
                      h="40px"
                    />
                    {errors.password && (
                      <FormErrorMessage fontSize="12px">
                        {errors.password}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    w="full"
                    h="40px"
                    bg="primary.500"
                    color="white"
                    fontWeight="600"
                    fontSize="14px"
                    borderRadius="8px"
                    _hover={{
                      bg: "primary.600",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(26, 94, 63, 0.3)",
                    }}
                    _active={{
                      transform: "translateY(0)",
                      boxShadow: "0 2px 4px rgba(26, 94, 63, 0.2)",
                    }}
                    isLoading={isLoading}
                    transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    mt={2}
                  >
                    Sign In
                  </Button>
                </VStack>
              </form>

              {/* Divider */}
              <Box h="1px" bg="neutral.200" w="full" />

              {/* Register Link */}
              <VStack spacing={2} align="center">
                <Text fontSize="14px" color="neutral.600">
                  Don't have an account?{" "}
                  <Link
                    as={RouterLink}
                    to="/register"
                    color="primary.500"
                    fontWeight="600"
                    _hover={{ color: "primary.600", textDecoration: "none" }}
                  >
                    Register here
                  </Link>
                </Text>
              </VStack>
            </VStack>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Login;
